/* eslint-disable max-lines */
import { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import Menu from '@base/Menu/Menu';
import { useDispatchToStore } from '@hooks/reduxHooks';
import getPlusSubscriptionsUids from '@utils/subscriptionHelpers/getPlusSubscriptionUIDs';
import pushRoute from '@utils/pushRoute';
import { fetchCourseReviews, removeCourseFromPlanner } from '@actions/courses';
import PlusHeaderSkeleton from '@skeletons/Course/PlusHeader';
import { segment } from '@actions/analytics';
import { fetchRenewalTouchPoint } from '@actions/auth';
import ScheduleSkeleton from '@skeletons/Course/Schedule';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { useSnackbar } from '@stores/snackbar';
import {
  unlockFreePlanFor,
  programmeTypes,
  evtNameCategoryMap,
  staticStrings,
  getCourseEnrollMessage
} from '@constants/index';
import Dynamic from '@base/Dynamic/Dynamic';
import Dialog from '@comp/Dialog/Dialog';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useCourseClassPlayer from '@hooks/useCourseClassPlayer';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import {
  ADD_TO_PLANNER_REMOVED,
  TEST_SERIES_ENROLLED,
  TEST_SERIES_UNENROLLED,
  COURSE_UNENROLLED,
  COURSE_ENROLLED,
  TEST_SERIES_KEYS,
  COURSE_KEYS,
  COURSE_VIEWED,
  TEST_SERIES_VIEWED
} from '@constants/segment';
import { emptyList } from '@constants/empty';
import useCheckFeatureLimit from '@cont/DeviceLimitation/useCheckFeatureLimit';
import { FeatureType } from 'types/features';
import {
  getIsSubscribedToLite,
  getIsSubscribedToPlus,
  getIsSubscribedToOffline,
  getIsSubscribedToIconic,
  getIsSubscribedToIconicPro
} from '@selectors/subscription';
import { getIsLoggedIn } from '@selectors/auth';
import { getIsGTPGoal } from '@api/selectors/constants';
import useCourseInfo from '@api/hooks/course/useCourseInfo';
import useSimilarPlusCourses from '@api/hooks/course/useSimilarPlusCourses';
import useRelevantFreeCourses from '@api/hooks/course/useRelevantFreeCourses';
import useLessonList from '@api/hooks/course/useLessonList';
import useCourseTrack from '@api/hooks/course/useCourseTrack';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import useConstants from '@api/hooks/useConstants';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';
import { courseTypes } from '@actions-constants/index';
import useUAM, {
  UAM_ACTIONS,
  UAM_RESOURCE_TYPES
} from '@api/hooks/auth/useUAM';
import CourseDemoClasses from '@cont/Batch/DemoClasses/DemoClasses';
import segmentStorage from '@events/segmentStorage';
import {
  getIsNonOfflineHybridCourse,
  getIsOnlyRecordedClassesPresent
} from '@api/selectors/course';
import { getCourseDemoClassesKey } from '@api/hooks/batch/useDemoClasses';
import { EDUCATOR_TYPES } from '@cont/Batch/utils';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe } from '@api/selectors/auth';
import useLiveClassAndCourseList from '@api/hooks/educator/useLiveClassAndCourseList';
import { getLiveAndByWeekLessons } from './utils';
import PDFDownload from './PDFDownload';
import LearnerSideCourseSchdule from './LearnerSideCourseSchdule';
import PlusCourseHeader from './CourseHeader/PlusCourseHeader';

const DppFinishSummary = Dynamic(() =>
  import('@cont/Practice/Listing/DppFinishSummary')
);

const Carousel = Dynamic(() => import('@comp/Carousel/Carousel'));
const Reviews = Dynamic(() => import('./Reviews'));
const PlusCourseCard = Dynamic(() =>
  import('@comp/PlusCourseCard/PlusCourseCard')
);
const FreePlanUnlock = Dynamic(() =>
  import('@comp/FreePlanUnlock/FreePlanUnlock')
);
const FreeCourseHeader = Dynamic(() => import('./FreeCourseHeader'), {
  loading: PlusHeaderSkeleton
});
const FreeCourseCard = Dynamic(() =>
  import('@comp/FreeCourseCard/FreeCourseCard')
);

const StyledCarousel = styled(Carousel)`
  @media (max-width: 47.95em) {
    margin-top: 80px !important;
  }
`;

const Course = ({ courseUID, isTestSeries, freeTrialData }) => {
  const {
    course,
    mutate: mutateCourseData,
    onToggleEnroll: onToggleCourseEnroll,
    isValidating
  } = useCourseInfo(courseUID);
  const { similarCourses } = useSimilarPlusCourses(courseUID);
  const { relevantFreeCourses } = useRelevantFreeCourses(courseUID);
  const { lessonList, mutate: mutateLessonListData } = useLessonList(courseUID);
  const { courseTrack, mutate: mutateNextTrack } = useCourseTrack(courseUID);
  const { hasAccess: hasAccessToCourse, mutate: mutateCourseUAM } = useUAM(
    UAM_ACTIONS.PROGRAMME_VIEW,
    UAM_RESOURCE_TYPES.PROGRAMME,
    courseUID
  );
  const selectedGoalUID = useSelectedGoalUID();

  const { goalUid: goalUID, forPlus, courseType } = course;

  const isHyridNonOffline = getIsNonOfflineHybridCourse(course);
  const isOnlyRecordedClassesPresent = getIsOnlyRecordedClassesPresent(course);

  const dispatch = useDispatchToStore();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [freePlanUnlockDrawerState, setFreePlanUnlockDrawerState] =
    useState(false);

  const closeFreePlanUnlockDrawer = () => setFreePlanUnlockDrawerState(false);
  const [objectUid, setObjectUid] = useState(null);

  const renewalData = useSelector(({ auth }) => auth.renewalData?.[goalUID]);

  const me = useSelector(({ auth }) => auth.me);

  const isLoggedIn = useSelector(getIsLoggedIn);

  const reviews = useSelector(
    ({ paginator }) => paginator.courseReviews?.[courseUID]
  );

  const goalData = useSelector(({ topologies }) => topologies.data[goalUID]);
  const goalName = goalData?.name;

  const isLiteUser = useSelector(getIsSubscribedToLite(goalUID));
  const meData = useMyInfo(getMe);
  const isPlusUser =
    useSelector(getIsSubscribedToPlus(goalUID)) ||
    getPlusSubscriptionsUids(meData).includes(goalUID?.toUpperCase());

  const isGTP = useConstants(getIsGTPGoal(goalUID));

  const resumeLesson = courseTrack.nextTrack;
  const resumeQuiz = courseTrack.activeQuizSession;
  const isPlatformUnlocked = me.platformUnlockStatus?.is_platform_unlocked;
  const allLessons = lessonList.results || emptyList;
  const lessonsExists = !!allLessons.length;
  const eventKeys =
    courseType === programmeTypes.TESTSERIES ? TEST_SERIES_KEYS : COURSE_KEYS;
  const goalSegmentData = useGoalEventProps({ goalUID });
  const segmentData = useAllTypesOfCourseEventProps({
    course,
    isTesSries: courseType === programmeTypes.TESTSERIES
  });

  const { open: openClassPlayer } = useCourseClassPlayer();
  const { open: showSnackbar } = useSnackbar();

  const isRecordedGoal = useIsRecordedGoal(goalUID);

  const { liveClassList } = useLiveClassAndCourseList();

  const setPDF = (elem, lesson) => {
    setAnchorEl(elem);
    setSelectedLesson(lesson);
  };
  const isOfflineSubscription = useSelector(getIsSubscribedToOffline(goalUID));
  const isIconicUser = useSelector(getIsSubscribedToIconic(goalUID));
  const isIconicProUser = useSelector(getIsSubscribedToIconicPro(goalUID));
  const isStorePurchase =
    hasAccessToCourse &&
    !(isPlusUser || isOfflineSubscription || isIconicUser || isIconicProUser);

  useEffect(() => {
    segment.track(
      courseType === programmeTypes.TESTSERIES
        ? TEST_SERIES_VIEWED
        : COURSE_VIEWED,
      {
        ...goalSegmentData,
        ...segmentData,
        course_state: course.state,
        is_offline_course: course?.courseType === programmeTypes.HYBRID_COURSE,
        is_offline_test_series:
          course?.courseType === programmeTypes.HYBRID_TESTSERIES,
        is_store_exclusive: course?.isStoreExclusive,
        educator_type:
          course?.educatorType === EDUCATOR_TYPES.PARTNER ? 'Partner' : 'Plus',
        last_primary_source_section: segmentStorage.getItem('lpss')
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      eventKeys
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!forPlus && !reviews?.[courseUID]) {
      dispatch(fetchCourseReviews(courseUID));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseUID]);

  useEffect(() => {
    if (isLoggedIn) {
      mutateCourseData();
      mutateNextTrack();
      mutateLessonListData();
      mutateCourseUAM();
    }

    if (forPlus && goalUID && isLoggedIn) {
      dispatch(fetchRenewalTouchPoint(goalUID));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, courseUID]);

  const [unEnrollModalOpen, setUnEnrollModalOpen] = useState(false);
  const isCompleted = useSelector(
    ({ courses }) =>
      courses.data[course.uid]?.isCompleted === 1 || course.isCompleted
  );

  /**
   * Trigger only event if it's user initiated action.
   * @param {boolean} triggerAnalytics Should we send analytics for this event
   */
  const handleRemoveFromPlanner = async (triggerAnalytics = false) => {
    dispatch(removeCourseFromPlanner(courseUID));
    if (triggerAnalytics) {
      segment.track(
        ADD_TO_PLANNER_REMOVED,
        { ...goalSegmentData, ...segmentData },
        evtNameCategoryMap.WITHOUT_CATEGORY,
        true,
        eventKeys
      );
    }
  };

  const sendSegmentEnrollEvent = () => {
    const eventName =
      courseType === programmeTypes.TESTSERIES
        ? TEST_SERIES_ENROLLED
        : COURSE_ENROLLED;
    segment.track(
      eventName,
      {
        ...goalSegmentData,
        ...segmentData
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      eventKeys
    );
  };

  const { openElement: showLoginDrawer } = useToggleable(LOGIN_DRAWER);

  const handleCourseToggle = async () => {
    if (!isLoggedIn) {
      showLoginDrawer();

      return;
    }

    if (course.isEnrolled) {
      setUnEnrollModalOpen(true);

      return;
    }

    sendSegmentEnrollEvent();

    const resp = await onToggleCourseEnroll();

    if (resp?.error) {
      showSnackbar({
        message: resp.error.message || 'Something went wrong',
        type: 'error'
      });

      return;
    }

    // Update the course enrolled state in Redux store as well
    const reduxCourseEnrollToggleAction = `${courseTypes.TOGGLE_COURSE_ENROLLED}_SUCCESS`;
    dispatch({
      type: reduxCourseEnrollToggleAction,
      enroll: true,
      courseUID: course.uid
    });

    if (!isOnlyRecordedClassesPresent) {
      showSnackbar({
        message: isLiteUser
          ? staticStrings.UNENROLL_MESSAGE_FOR_LITE_COURSES
          : getCourseEnrollMessage({
              type: isTestSeries ? 'Test-series' : 'Course',
              goalUID,
              selectedGoalUID,
              goalName,
              isRecordedGoal,
              isHyridNonOffline
            }),
        type: 'success'
      });
    }
  };

  const sendSegmentUnEnrollEvent = () => {
    const eventName =
      courseType === programmeTypes.TESTSERIES
        ? TEST_SERIES_UNENROLLED
        : COURSE_UNENROLLED;
    segment.track(
      eventName,
      {
        ...goalSegmentData,
        ...segmentData
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      eventKeys
    );
  };

  const acceptOnClick = async () => {
    sendSegmentUnEnrollEvent();

    if (!isTestSeries && isCompleted) {
      handleRemoveFromPlanner();
    }

    await onToggleCourseEnroll();

    // Update the course enrolled state in Redux store as well
    const reduxCourseEnrollToggleAction = `${courseTypes.TOGGLE_COURSE_ENROLLED}_SUCCESS`;
    dispatch({
      type: reduxCourseEnrollToggleAction,
      enroll: false,
      courseUID: course.uid
    });

    setUnEnrollModalOpen(false);
  };

  const onCloseModal = () => setUnEnrollModalOpen(false);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const onItemClick = useCheckFeatureLimit(FeatureType.CLASS, (lesson = {}) => {
    if (lesson.type === 'post') {
      if (course?.forPlus && isLoggedIn) {
        if (isPlatformUnlocked) {
          segmentStorage.setItem('lpss', 'lesson card');
          openClassPlayer({
            lesson,
            course,
            forceFetch: isGTP
          });
        } else {
          setObjectUid(lesson.uid);
          if (lesson.isSpecial || lesson.liveClassDetails?.isSpecial) {
            handleCourseToggle();
          } else {
            setFreePlanUnlockDrawerState(true);
          }
        }
      } else if (course?.forPlus && !isLoggedIn) {
        // don't redirect anywhere
      } else {
        pushRoute(
          '/lesson/[lessonSlug]/[lessonUID]',
          `/lesson/${lesson.slug}/${lesson.uid}`
        );
      }
    } else if (lesson.totalQuestions > 0)
      pushRoute(
        '/quiz/[quizSlug]/[quizUID]',
        `/quiz/${lesson.slug}/${lesson.uid}`
      );
  });

  const renderHeader = () => {
    if (!course.uid) {
      return <PlusHeaderSkeleton />;
    }

    if (forPlus) {
      return (
        <PlusCourseHeader
          handleRemoveFromPlanner={handleRemoveFromPlanner}
          lessonList={lessonList}
          courseUid={course?.uid}
          course={course}
          goalUid={goalData?.uid}
          toggleEnroll={handleCourseToggle}
          isTestSeries={isTestSeries}
          isLoading={isValidating}
          isRecordedGoal={isRecordedGoal}
          hideCTA={isStorePurchase}
          freeTrialData={freeTrialData}
        />
      );
    }

    return <FreeCourseHeader course={course} />;
  };

  const renderLessons = () => {
    if (!lessonsExists) {
      return <ScheduleSkeleton />;
    }

    const lessonInfo = getLiveAndByWeekLessons(allLessons, liveClassList);
    const lessonsCount = allLessons.length || 0;
    const showByWeek = forPlus
      ? allLessons[0]?.week !== 0 &&
        lessonsCount > 10 &&
        lessonsCount / allLessons[lessonsCount - 1].week > 2
      : false;

    return (
      <>
        <LearnerSideCourseSchdule
          course={course}
          isTestSeries={isTestSeries}
          allLessons={allLessons}
          lessonByWeeks={lessonInfo.byWeek}
          lessonToResume={resumeQuiz || resumeLesson}
          liveLesson={lessonInfo.liveLesson}
          onItemClick={onItemClick}
          setAnchorEl={setPDF}
          selectedLesson={selectedLesson}
          renewalData={renewalData}
          showByWeek={showByWeek}
          hasDppAccess={hasAccessToCourse}
          liveClassList={liveClassList}
        />
        <DppFinishSummary source="Course" />
      </>
    );
  };

  return (
    <>
      <Dialog
        onClose={onCloseModal}
        dialogState={unEnrollModalOpen}
        heading="Are you sure you want to unenroll?"
        message={`This ${
          isTestSeries ? 'test-series' : 'course'
        } will be removed from your schedule and you will stop receiving updates related to this course`}
        acceptLabel="Unenroll"
        rejectLabel="Cancel"
        acceptOnClick={acceptOnClick}
        rejectOnClick={onCloseModal}
      />
      <Menu
        anchorEl={anchorEl}
        onClose={handleClose}
        position="right"
        width="231px"
      >
        <PDFDownload
          selectedLesson={selectedLesson}
          handleClose={handleClose}
          me={me}
          source="learnerCoursePage"
          course={course}
          goal={goalData}
        />
      </Menu>
      {renderHeader()}
      <CourseDemoClasses
        apiKey={getCourseDemoClassesKey(courseUID)}
        goalUID={goalUID}
      />
      {renderLessons()}
      {similarCourses?.data && (
        <StyledCarousel
          label={similarCourses.extraBlockinfo?.title || 'Similar Courses'}
          key={similarCourses.extraBlockinfo?.title || 'Similar Courses'}
          showControls={false}
          numberOfItems={4}
          gridColumn="span 24"
        >
          {similarCourses.data?.map((courseData) => (
            <PlusCourseCard
              gridColumn="span 6/ auto"
              key={courseData.uid}
              course={courseData}
              dataAnalytics="course-courseEnroll"
              newTab
            />
          ))}
        </StyledCarousel>
      )}

      {reviews?.results?.length > 0 && (
        <Reviews course={course} reviews={reviews} />
      )}
      {!!relevantFreeCourses.length && (
        <StyledCarousel
          label="Similar courses"
          key="Similar courses"
          showControls={false}
          numberOfItems={4}
          gridColumn="span 24"
        >
          {relevantFreeCourses.map((freeCourse) => (
            <FreeCourseCard
              gridColumn="span 6/ auto"
              key={freeCourse.uid}
              course={freeCourse}
            />
          ))}
        </StyledCarousel>
      )}
      <FreePlanUnlock
        drawerState={freePlanUnlockDrawerState}
        closeDrawerHandler={closeFreePlanUnlockDrawer}
        objectType={unlockFreePlanFor.SPECIAL_CLASS}
        objectUid={objectUid}
        callBack={() => {
          setTimeout(() => setFreePlanUnlockDrawerState(false), 3000);
          handleCourseToggle();
        }}
      />
    </>
  );
};

export default Course;
